import React, { useEffect, useState } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Loader2Icon, SparklesIcon } from "lucide-react";
import { BillingService } from "../../services/BillingService";
import { usePermissions } from "../../PermissionsProvider";

interface Plan {
  key: string;
  name: string;
  price_usd: number;
  credits: number;
  description: string;
}

interface Subscription {
  plan_key: string;
  status: string;
  current_period_end: number;
  cancel_at_period_end: boolean;
}

const SubscriptionPlans: React.FC = () => {
  const { apiKey } = usePermissions();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);

  useEffect(() => {
    if (!apiKey) {
      return;
    }

    // Store subscriptions for cleanup
    const plansSubscription = BillingService.getSubscriptionPlans(
      null,
      apiKey
    ).subscribe(
      (result: Plan[]) => {
        setPlans(result);
        setLoading(false);
      },
      (error: any) => {
        console.error("Error fetching plans:", error);
        setLoading(false);
      }
    );

    const subscriptionSubscription = BillingService.getSubscription(
      null,
      apiKey
    ).subscribe(
      (result: Subscription) => {
        setSubscription(result);
      },
      (error: any) => {
        // No active subscription is fine
        setSubscription(null);
      }
    );

    // Cleanup function - unsubscribe on unmount
    return () => {
      plansSubscription.unsubscribe();
      subscriptionSubscription.unsubscribe();
    };
  }, [apiKey]);

  const handleSubscribe = (planKey: string) => {
    const baseUrl = window.location.origin;
    const successUrl = `${baseUrl}/billing?success=true`;
    const cancelUrl = `${baseUrl}/billing`;

    setCheckoutLoading(planKey);

    BillingService.createCheckoutSession(
      planKey,
      successUrl,
      cancelUrl,
      null,
      apiKey
    ).subscribe(
      (result: { checkout_url: string }) => {
        window.location.href = result.checkout_url;
      },
      (error: any) => {
        console.error("Error creating checkout session:", error);
        setCheckoutLoading(null);
      }
    );
  };

  const handleManageSubscription = () => {
    const returnUrl = `${window.location.origin}/billing`;

    BillingService.createCustomerPortalSession(
      returnUrl,
      null,
      apiKey
    ).subscribe(
      (result: { portal_url: string }) => {
        window.location.href = result.portal_url;
      },
      (error: any) => {
        console.error("Error creating portal session:", error);
      }
    );
  };

  const isCurrentPlan = (planKey: string) => {
    return (
      subscription?.plan_key === planKey && subscription?.status === "active"
    );
  };

  const hasActiveSubscription = subscription?.status === "active";

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2Icon className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-gc-med text-foreground">
          Subscription Plans
        </h3>
        <p className="text-sm text-muted-foreground">
          Choose a plan that fits your needs
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Pay As You Go Plan */}
        <Card
          className={`border-2 ${
            !hasActiveSubscription
              ? "border-maitai-green bg-maitai-green/5"
              : "border-border"
          }`}>
          <CardContent className="p-6 space-y-6">
            {!hasActiveSubscription && (
              <div className="flex items-center gap-2">
                <div className="px-2 py-1 bg-maitai-green text-white text-xs font-gc-med rounded">
                  CURRENT
                </div>
              </div>
            )}

            <div className="space-y-2">
              <h4 className="text-xl font-gc-demi-bold text-foreground">
                Pay As You Go
              </h4>
              <p className="text-sm text-muted-foreground">
                No commitments. Manually or automatically add credits as you
                need them.
              </p>
            </div>

            <Button
              variant={!hasActiveSubscription ? "default" : "outline"}
              className="w-full font-gc-med"
              disabled={!hasActiveSubscription}
              onClick={
                hasActiveSubscription ? handleManageSubscription : undefined
              }>
              {!hasActiveSubscription
                ? "Current Plan"
                : "Switch to Pay As You Go"}
            </Button>
          </CardContent>
        </Card>

        {/* Subscription Plans */}
        {plans.map((plan) => (
          <Card
            key={plan.key}
            className={`border-2 ${
              isCurrentPlan(plan.key)
                ? "border-maitai-green bg-maitai-green/5"
                : "border-border"
            }`}>
            <CardContent className="p-6 space-y-6">
              {isCurrentPlan(plan.key) && (
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <div className="px-2 py-1 bg-maitai-green text-white text-xs font-gc-med rounded">
                      CURRENT
                    </div>
                    {subscription?.cancel_at_period_end && (
                      <div className="px-2 py-1 bg-orange-500 text-white text-xs font-gc-med rounded">
                        CANCELING
                      </div>
                    )}
                  </div>
                  {subscription?.cancel_at_period_end &&
                    subscription?.current_period_end && (
                      <div className="text-xs text-muted-foreground">
                        Active until{" "}
                        {new Date(
                          subscription.current_period_end * 1000
                        ).toLocaleDateString()}
                      </div>
                    )}
                </div>
              )}

              <div className="space-y-2">
                <h4 className="text-xl font-gc-demi-bold text-foreground">
                  {plan.name}
                </h4>
                <div className="flex items-baseline gap-1">
                  <span className="text-3xl font-gc-bold text-foreground">
                    ${plan.price_usd.toFixed(0)}
                  </span>
                  <span className="text-sm text-muted-foreground">/month</span>
                </div>
                <p className="text-sm text-muted-foreground">
                  {plan.description}
                </p>
              </div>

              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <SparklesIcon className="w-5 h-5 text-maitai-green flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-foreground font-gc-med">
                    {plan.credits.toLocaleString()} credits per month
                  </span>
                </div>
              </div>

              <Button
                variant={isCurrentPlan(plan.key) ? "outline" : "default"}
                className="w-full font-gc-med"
                disabled={checkoutLoading === plan.key}
                onClick={() =>
                  isCurrentPlan(plan.key)
                    ? handleManageSubscription()
                    : handleSubscribe(plan.key)
                }>
                {checkoutLoading === plan.key ? (
                  <>
                    <Loader2Icon className="w-4 h-4 mr-2 animate-spin" />
                    Loading...
                  </>
                ) : isCurrentPlan(plan.key) ? (
                  "Manage Subscription"
                ) : (
                  `Subscribe to ${plan.name}`
                )}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default SubscriptionPlans;
